import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output} from '@angular/core';

type SheetState = 'initial' | 'expanded';

@Directive({
  selector: '[appBottomSheetDrag]',
  standalone: true,
})
export class BottomSheetDragDirective implements AfterViewInit, OnDestroy {

  @Input() dragEnabled = true;
  @Output() sheetClose = new EventEmitter<void>();

  private el: HTMLElement;

  private state: SheetState = 'initial';

  private initialHeight = 0;
  private maxHeight = 0;
  private currentHeight = 0;

  private startY = 0;
  private startHeight = 0;
  private isDragging = false;

  private isMobileTablet = false;
  private cleanupFns: (() => void)[] = [];

  private readonly THRESHOLD = 50;

  constructor(private elementRef: ElementRef<HTMLElement>) {
    this.el = this.elementRef.nativeElement;
  }

  ngAfterViewInit(): void {
    this.isMobileTablet = window.matchMedia('(max-width: 1023px)').matches;

    if (!this.isMobileTablet) return;

    this.initHeights();
    this.applyHeight(this.currentHeight);

    this.el.style.transition = 'height 0.3s ease-out';

    this.addListener('touchstart', this.onTouchStart as EventListener);
    this.addListener('touchmove', this.onTouchMove as EventListener);
    this.addListener('touchend', this.onTouchEnd as EventListener);

    const resizeHandler = () => this.onResize();
    window.addEventListener('resize', resizeHandler);
    this.cleanupFns.push(() => window.removeEventListener('resize', resizeHandler));
  }

  ngOnDestroy(): void {
    this.cleanupFns.forEach(fn => fn());
  }

  // ================= INIT =================

  private initHeights() {
    const contentHeight = this.el.scrollHeight;
    const max = Math.floor(window.innerHeight * 0.99);

    this.maxHeight = max;
    this.initialHeight = Math.min(contentHeight, max);
    this.currentHeight = this.initialHeight;

    if (this.initialHeight >= this.maxHeight - 1) {
      this.state = 'expanded';
    }
  }

  private onResize() {
    this.maxHeight = Math.floor(window.innerHeight * 0.99);

    if (this.state === 'expanded') {
      this.applyHeight(this.maxHeight);
    } else {
      this.applyHeight(Math.min(this.initialHeight, this.maxHeight));
    }
  }

  // ================= EVENTS =================

  private addListener(type: string, handler: EventListener) {
    this.el.addEventListener(type, handler, { passive: false });
    this.cleanupFns.push(() => this.el.removeEventListener(type, handler));
  }

  private applyHeight(height: number) {
    this.currentHeight = height;
    this.el.style.height = `${height}px`;
  }

  private onTouchStart = (e: TouchEvent) => {
    
   /*  const isHandle = (e.target as HTMLElement).closest('.');
    if (!isHandle) return; */

    this.isDragging = true;
    this.startY = e.touches[0].clientY;
    this.startHeight = this.currentHeight;

    this.el.style.transition = 'none';
  };

  private onTouchMove = (e: TouchEvent) => {

    if (!this.isDragging || !this.dragEnabled) return;

    const deltaY = this.startY - e.touches[0].clientY;
    let next = this.startHeight + deltaY;

    next = Math.max(this.initialHeight, Math.min(next, this.maxHeight));

    this.applyHeight(next);

    if ((this.currentHeight < this.maxHeight || deltaY < 0) && e.cancelable) {
      e.preventDefault();
    }
  };

  private onTouchEnd = (e: TouchEvent) => {
    if (!this.isDragging) return;

    this.isDragging = false;
    this.el.style.transition = 'height 0.3s ease-out';

    const dragDistance = this.startY - e.changedTouches[0].clientY;

    if (dragDistance > this.THRESHOLD) {
      if (this.currentHeight >= this.maxHeight - 10) {
        this.state = 'expanded';
        this.applyHeight(this.maxHeight);
      }
      return;
    }

    if (dragDistance < -this.THRESHOLD) {
      if (this.state === 'expanded') {
        this.state = 'initial';
        this.applyHeight(this.initialHeight);
      } else {
        this.sheetClose.emit();
      }
      return;
    }

    // snap back
    this.applyHeight(
      this.state === 'expanded' ? this.maxHeight : this.initialHeight
    );
  };
}